import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  Dispatch,
} from 'react';
import { AppState, MutationAction, Order } from './types'; // Move types to a shared file
import { reducer, initialState } from './reducer';

// 1. Separate State and Dispatch Contexts
const StateContext = createContext<AppState | undefined>(undefined);
const DispatchContext = createContext<Dispatch<MutationAction> | undefined>(
  undefined,
);

export const DefaultLocalCart: Order = {
  recipient: {},
  lines: [],
  deliveryMethod: null,
  id: null,
};

export const CART_STORAGE_KEY = 'ttc_local';

const getInitialState = (): AppState<any> => {
  let localCart;
  if (typeof window === 'undefined') {
    localCart = DefaultLocalCart;
  } else {
    try {
      let stored = localStorage.getItem(CART_STORAGE_KEY);
      localCart = stored ? JSON.parse(stored) : DefaultLocalCart;
    } catch (error) {
      console.error('Failed to parse local cart', error);
      localCart = DefaultLocalCart;
    }
  }

  return { ...initialState, order: localCart };
};

export const AppStateProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, getInitialState());

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
};

// 2. Custom Hooks with Invariant Checks
export const useAppState = () => {
  const context = useContext(StateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};

export const useAppDispatch = () => {
  const context = useContext(DispatchContext);
  if (context === undefined) {
    throw new Error('useAppDispatch must be used within an AppStateProvider');
  }
  return context;
};
